<template>
  <v-container data-app>
    <div id="container"></div>
    <Birdcard
      v-model="showBirdCard"
      :dialog="showBirdCard"
      :musicOn="musicOn"
      @close="
        showBirdCard = false;
        checkIfLastBird();
      "
      :bird="birdCardText"
      @toggle-music="$emit('toggle-music')"
      :players="players"
      style="z-index: 9999"
    />
    <Rewardcard
      v-model="showRewardCard"
      :dialog="showRewardCard"
      @close="showRewardCard = false"
      style="z-index: 9999"
    />
    <v-container class="bottom-container">
      <v-row v-if="birdsFound" align="center" justify="center">
        <v-card
          class="bird-found-card"
          v-for="(bird, index) in birdsData"
          :key="index"
          width="45px"
          height="45px"
          :color="
            bird.showInDayMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'
          "
        >
          <img
            @click="goToBird(bird.position)"
            v-if="birdsFound.includes(bird.name)"
            :src="require(`../assets/bird-pics/${bird.url}.png`)"
            bird-pics
            style="height: 40px; width: 40px; z-index: 9999"
            :alt="bird.name"
          />
        </v-card>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Water } from "three/examples/jsm/objects/Water.js";
import { Sky } from "three/examples/jsm/objects/Sky.js";

import { Loop } from "../Loop.js";
import Birdcard from "./BirdCard.vue";
import Rewardcard from "./RewardCard.vue";
import birdsData from "../bird_infos.json";
import * as Tone from "tone";

export default {
  name: "Scene",
  components: { Birdcard, Rewardcard },
  props: {
    showOnboarding: Boolean,
    isLoading: Boolean,
    players: Object,
    glbdata: Object,
    musicOn: Boolean,
  },
  data() {
    return {
      rewarded: false,
      showBirdCard: false,
      showRewardCard: false,
      isDay: true,
      birdCardText: birdsData[0],
      camera: null,
      renderer: null,
      scene: null,
      controls: null,
      loop: null,
      raycaster: null,
      mouse: { x: 0, y: 0 },
      sprites: [],
      ambientLight: null,
      mainLight: null,
      water: null,
      sun: null,
      sky: null,
      sunParameters: null,
      birdsFound: [],
      birdsData: birdsData,
    };
  },
  mounted() {
    this.$emit("loading");

    this.container = document.getElementById("container");
    this.createCamera();
    this.createRenderer();
    this.createScene();
    this.createWaterAndSky();
    this.loop = new Loop(this.camera, this.scene, this.renderer, this.water);
    this.container.append(this.renderer.domElement);
    this.createControls();
    this.createLights();
    this.loop.updatables.push(this.controls);
    this.scene.add(this.ambientLight, this.mainLight);
    this.init();
    this.addBirdSprites();
    this.renderer.render(this.scene, this.camera);

    this.loop.start();
  },
  methods: {
    checkIfLastBird() {
      if (this.birdsFound.length == 12 && !this.rewarded) {
        this.showRewardCard = true;
        this.rewarded = true;
        const p = new Tone.Player(
          require("../assets/reward_sound.wav")
        ).toDestination();
        Tone.loaded().then(() => {
          p.start();
        });
      }
    },
    goToBird(pos) {
      this.camera.position.set(pos[0], pos[1], pos[2]);
    },
    async init() {
      try {
        var grassMat = new THREE.MeshBasicMaterial({
          color: 0x7d90ff,
        });
        var reedMaterial = new THREE.MeshBasicMaterial({
          color: 0xe8fb53,
        });

        /** reeds and grass */
        const reeds = this.setupModel(this.glbdata.reedsData, reedMaterial);
        const grass = this.setupModel(this.glbdata.grassData, grassMat);

        /** tree */
        const tree = this.setupModel(this.glbdata.treeData);
        const treeLeaves = this.setupModel(this.glbdata.treeLeavesData);
        const treeLeaves2 = this.setupModel(this.glbdata.treeLeavesData2);
        const trees = this.setupModel(this.glbdata.treesData);
        const trees2 = this.setupModel(this.glbdata.treesData2);
        const leaves = this.setupModel(this.glbdata.leavesData);

        /** bold Trees */
        const boldTrees = this.setupModel(this.glbdata.boldTreesData);
        const boldTrees2 = this.setupModel(this.glbdata.boldTreesData2);
        const boldTrees3 = this.setupModel(this.glbdata.boldTreesData3);
        const boldTrees4 = this.setupModel(this.glbdata.boldTreesData4);

        /** pine trees */
        const pines = this.setupModel(this.glbdata.pinesData);
        const pines2 = this.setupModel(this.glbdata.pinesData2);
        const pines3 = this.setupModel(this.glbdata.pinesData3);
        const pineTrunks = this.setupModel(this.glbdata.pineTrunksData);

        /** birches */
        const birches = this.setupModel(this.glbdata.birchesData);
        const birchTrunks = this.setupModel(this.glbdata.birchTrunksData);

        /** bushes */
        const bushes = this.setupModel(this.glbdata.bushesData);
        const bushes2 = this.setupModel(this.glbdata.bushesData2);
        const bushesIsland = this.setupModel(this.glbdata.bushesIslandData);

        /** landscape */
        const landscape = this.setupModel(this.glbdata.landscapeData);

        this.controls.target.copy(this.water.position);

        this.scene.add(
          landscape,
          leaves,
          tree,
          treeLeaves,
          treeLeaves2,
          trees,
          trees2,
          boldTrees,
          boldTrees2,
          boldTrees3,
          boldTrees4,
          reeds,
          pines,
          pines2,
          pines3,
          pineTrunks,
          birches,
          birchTrunks,
          grass,
          bushes,
          bushes2,
          bushesIsland
        );
        this.$emit("loaded");

        this.raycaster = new THREE.Raycaster(); // create once
        this.mouse = new THREE.Vector2(); // create once
        // when the mouse moves, call the given function
        document.addEventListener("mousedown", this.onDocumentMouseDown, false);
        this.controls.addEventListener("change", () =>
          this.renderer.render(this.scene, this.camera)
        );
      } catch (e) {
        console.log("error", e);
      }
    },
    addBirdSprites() {
      this.dayLayer = new THREE.Object3D();
      this.nightLayer = new THREE.Object3D();
      this.scene.add(this.layerDay);

      const textureLoader = new THREE.TextureLoader();
      birdsData.forEach((bird) => {
        if (bird.position) {
          const mapC = textureLoader.load("bird-pics/" + bird.url + ".png");
          const material = new THREE.SpriteMaterial({
            map: mapC,
            color: 0xffc0cb,
            fog: true,
          });
          const sprite = new THREE.Sprite(material);
          sprite.name = bird.url;
          sprite.position.set(...bird.position);
          sprite.scale.set(2, 2, 2);
          this.sprites.push(sprite);
          if (bird.showInDayMode) {
            this.dayLayer.add(sprite);
          } else {
            this.nightLayer.add(sprite);
          }
          this.scene.add(this.dayLayer);
        }
      });
    },
    setupModel(data, color = null) {
      const model = data.scene.children[0];
      data.scene.traverse(function (object) {
        if (object instanceof THREE.Mesh) {
          object.material.metalness = 0;
          if (color) {
            object.material = color;
          }
          // object.material.flatShading = THREE.SmoothShading;
          // object.geometry.computeVertexNormals(true);
          object.castShadow = false;
          // object.matrixAutoUpdate = false;
          // object.receiveShadow = true;
        }
      });

      if (data.animations.length !== 0) {
        const clip = data.animations[0];
        const mixer = new THREE.AnimationMixer(model);
        const action = mixer.clipAction(clip);
        action.play();
        model.tick = (delta) => mixer.update(delta);
        // this.loop.updatables.push(model);
      }
      return model;
    },
    createCamera() {
      this.camera = new THREE.PerspectiveCamera(
        70,
        window.innerWidth / window.innerHeight,
        1, //near
        200 //far
      );
      this.camera.position.set(-11, 12, -40);
    },
    createScene() {
      this.scene = new THREE.Scene();

      this.scene.background = new THREE.Color("skyblue");
    },
    createRenderer() {
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: "high-performance",
      });
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.renderer.shadowMap.enabled = true;
      this.renderer.physicallyCorrectLights = true;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    },
    createControls() {
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);

      this.controls.enableDamping = true;
      this.controls.minDistance = 18.0;
      this.controls.maxDistance = 90.0;

      this.controls.autoRotate = true;
      this.controls.autoRotateSpeed = 0.05;
      this.controls.rotateSpeed = 0.5;
      this.controls.zoomSpeed = 0.5;

      // How far you can orbit vertically, upper and lower limits.
      this.controls.maxPolarAngle = Math.PI / 2.4; // radians
      this.controls.tick = () => this.controls.update();
    },

    createLights() {
      this.ambientLight = new THREE.HemisphereLight("darkblue", "white", 0.6);
      this.mainLight = new THREE.DirectionalLight("white", 1);
      this.mainLight.castShadow = true;
      this.mainLight.position.copy(this.sun);
      this.mainLight.position.y += 1;
      this.mainLight.target.position.set(100, 100, 100);
    },
    changeSun() {
      const steps = 30;
      let currStep = 0;
      let ambientShouldBe;
      let mainIntensityShouldBe;
      let sunParametersShouldBe;
      let pmremGenerator;

      if (this.isDay) {
        this.scene.remove(this.dayLayer);

        this.isDay = false;
        ambientShouldBe = 0.1;
        mainIntensityShouldBe = 0;
        sunParametersShouldBe = {
          elevation: 0,
          azimuth: 0,
        };

        this.ambientLight.color.set("darkblue");
      } else {
        this.scene.remove(this.nightLayer);

        this.isDay = true;
        ambientShouldBe = 0.6;
        mainIntensityShouldBe = 1;
        sunParametersShouldBe = {
          elevation: 8,
          azimuth: 180,
        };
        this.ambientLight.color.set("white");
      }
      this.sprites = [];

      pmremGenerator = new THREE.PMREMGenerator(this.renderer);

      /** calculate the step fro each paramater */
      let ambIntensityStep =
        (ambientShouldBe - this.ambientLight.intensity) / steps;
      let mainIntensityStep =
        (mainIntensityShouldBe - this.mainLight.intensity) / steps;
      let elevationStep =
        (sunParametersShouldBe.elevation - this.sunParameters.elevation) /
        steps;
      let azimuthStep =
        (sunParametersShouldBe.azimuth - this.sunParameters.azimuth) / steps;

      /** set interval to change the sun gradually */
      const interval = setInterval(() => {
        if (currStep == steps) {
          this.sunParameters = sunParametersShouldBe;
          this.renderer.render(this.scene, this.camera);
          if (this.isDay) {
            this.scene.add(this.dayLayer);
            this.dayLayer.children.forEach((child) => {
              this.sprites.push(child);
            });
          } else {
            this.scene.add(this.nightLayer);
            this.nightLayer.children.forEach((child) => {
              this.sprites.push(child);
            });
          }
          clearInterval(interval);
        }
        currStep += 1;
        this.sunParameters.elevation += elevationStep;
        this.sunParameters.azimuth += azimuthStep;
        this.updateSun(pmremGenerator, this.sunParameters);
        this.mainLight.position.set(this.sun.x, this.sun.y + 1, this.sun.z);

        this.ambientLight.intensity += ambIntensityStep;
        this.mainLight.intensity += mainIntensityStep;
        this.renderer.render(this.scene, this.camera);
      }, 50);
    },
    createWaterAndSky() {
      /** SUN */
      this.sun = new THREE.Vector3();

      /** WATER */
      const waterGeometry = new THREE.CircleGeometry(48, 48);
      this.water = new Water(waterGeometry, {
        textureWidth: 512,
        textureHeight: 512,
        waterNormals: new THREE.TextureLoader().load(
          "textures/waternormals.jpeg",
          function (texture) {
            texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
          }
        ),
        sunDirection: this.sun.normalize(),
        sunColor: 0xffffff,
        waterColor: 0x555c45,
        distortionScale: 10,
        fog: this.scene.fog !== undefined,
      });
      this.water.position.set(15, 15, 0);

      this.water.rotation.x = -Math.PI / 2;
      this.water.position.set(15, 0, 10);
      this.scene.add(this.water);

      /** SKY */
      this.sky = new Sky();
      this.sky.scale.setScalar(10000);
      this.scene.add(this.sky);

      const skyUniforms = this.sky.material.uniforms;

      skyUniforms["turbidity"].value = 10;
      skyUniforms["rayleigh"].value = 2;
      skyUniforms["mieCoefficient"].value = 0.005;
      skyUniforms["mieDirectionalG"].value = 0.8;

      // update sun
      this.sunParameters = {
        elevation: 8,
        azimuth: 180,
      };

      const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
      this.updateSun(pmremGenerator, this.sunParameters);

      window.addEventListener("resize", this.onWindowResize);
    },
    updateSun(pmremGenerator, sunParameters) {
      const phi = THREE.MathUtils.degToRad(90 - sunParameters.elevation);
      const theta = THREE.MathUtils.degToRad(sunParameters.azimuth);

      this.sun.setFromSphericalCoords(10, phi, theta);

      this.sky.material.uniforms["sunPosition"].value.copy(this.sun);
      this.water.material.uniforms["sunDirection"].value
        .copy(this.sun)
        .normalize();

      this.scene.environment = pmremGenerator.fromScene(this.sky).texture;
    },
    onWindowResize() {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(window.innerWidth, window.innerHeight);
    },
    onDocumentMouseDown(event) {
      if (!this.showOnboarding) {
        this.mouse.x =
          (event.clientX / this.renderer.domElement.clientWidth) * 2 - 1;
        this.mouse.y =
          -(event.clientY / this.renderer.domElement.clientHeight) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);

        var intersects = this.raycaster.intersectObjects(this.sprites);
        if (intersects[0] && this.$route.name == "Main") {
          birdsData.forEach((data) => {
            if (intersects[0].object.name == data.url) {
              this.birdCardText = data;
              this.birdsFound.includes(data.name)
                ? null
                : this.birdsFound.push(data.name);
              this.showBirdCard = true;
            }
          });
        }
      }
    },
  },
};
</script>

<style scoped>
#container {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
}
.v-app-bar {
  position: fixed;
  top: 2%;
}
.right-buttons {
  position: fixed;
  top: 0;
  right: 7%;
  width: 10%;
  display: flex;
  align-items: center;
  z-index: 9999;
}

.bottom-container {
  width: 100% !important;
  position: fixed;
  bottom: 5%;
  left: 50%;
  transform: translateX(-50%);
}
.bird-found-card {
  border-radius: 50%;
  margin: 2px;
  color: #fafafa;
}
</style>

